import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';

interface ExamSession {
  session_id: string;
  user_id: string;
  user_email?: string;
  user_name?: string;
  user_info?: { name?: string; email?: string };
  exam_type: string;
  status: 'active' | 'paused' | 'completed' | 'abandoned' | string;
  score?: number;
  current_question?: number;
  total_questions?: number;
  answered_questions?: number;
  time_taken_minutes?: number;
  started_at?: string;
  created_at?: string;
  completed_at?: string;
  questions?: Array<{ is_correct?: boolean }>;
  answers?: Record<string, unknown>;
  ttl?: number;
  ttl_expires_at?: string;
  ttl_expired?: boolean;
  time_remaining?: number;
}

const SESSIONS_PER_PAGE = 20;

const ExamMonitoringPage: React.FC = () => {
  const { user } = useAuth();
  const [examSessions, setExamSessions] = useState<ExamSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ExamSession | null>(null);
  const [sessionStatusFilter, setSessionStatusFilter] = useState('');
  const [sessionSearchEmail, setSessionSearchEmail] = useState('');
  const [sessionExamTypeFilter, setSessionExamTypeFilter] = useState('');
  const [sessionSearchId, setSessionSearchId] = useState('');
  const [sessionCurrentPage, setSessionCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [lastLoadMs, setLastLoadMs] = useState<number | null>(null);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalSessions / SESSIONS_PER_PAGE)), [totalSessions]);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadExamSessions = async (status = sessionStatusFilter, email = sessionSearchEmail, page = 1) => {
    setLoading(true);
    const startedAt = performance.now();
    try {
      const params = new URLSearchParams({
        limit: String(SESSIONS_PER_PAGE),
        page: String(page),
      });
      if (status && status !== 'all') params.set('status', status);
      if (email.trim()) params.set('email', email.trim());
      if (sessionExamTypeFilter.trim()) params.set('exam_type', sessionExamTypeFilter.trim());
      if (sessionSearchId.trim()) params.set('session_id', sessionSearchId.trim());

      const response = await apiCall(`/admin/exam-sessions?${params.toString()}`);
      if (response.ok) {
        const result = await response.json();
        setExamSessions(result.data.sessions || []);
        setTotalSessions(result.data.pagination?.total || 0);
        setSessionCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to load exam sessions:', error);
    } finally {
      setLastLoadMs(Math.round(performance.now() - startedAt));
      setLoading(false);
    }
  };

  const loadSessionDetails = async (sessionId: string) => {
    setDetailsLoading(true);
    try {
      const response = await apiCall(`/admin/exam-sessions/${sessionId}`);
      if (response.ok) {
        const result = await response.json();
        setSelectedSession(result.data);
      }
    } catch (error) {
      console.error('Failed to load session details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!window.confirm('이 시험 세션을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;

    try {
      const response = await apiCall(`/admin/exam-sessions/${sessionId}`, { method: 'DELETE' });
      if (response.ok) {
        setSelectedSession(null);
        loadExamSessions(sessionStatusFilter, sessionSearchEmail, sessionCurrentPage);
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  const bulkDeleteAbandoned = async () => {
    if (!window.confirm('중단된 모든 세션을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;

    try {
      const response = await apiCall('/admin/exam-sessions/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ status: 'abandoned' }),
      });
      if (response.ok) {
        loadExamSessions(sessionStatusFilter, sessionSearchEmail, 1);
      }
    } catch (error) {
      console.error('Failed to bulk delete sessions:', error);
    }
  };

  const completeSession = async (sessionId: string) => {
    if (!window.confirm('이 세션을 완료 상태로 변경하시겠습니까?')) return;

    try {
      const response = await apiCall(`/admin/exam-sessions/${sessionId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'completed' }),
      });
      if (response.ok) {
        loadExamSessions(sessionStatusFilter, sessionSearchEmail, sessionCurrentPage);
      }
    } catch (error) {
      console.error('Failed to update session status:', error);
    }
  };

  const generateAnalysis = async (sessionId: string) => {
    if (!window.confirm('AI 학습 분석을 생성하시겠습니까?')) return;

    try {
      const response = await apiCall(`/admin/exam-sessions/${sessionId}/generate-analysis`, { method: 'POST' });
      if (response.ok) {
        alert('AI 분석이 생성되었습니다.');
        if (selectedSession?.session_id === sessionId) {
          loadSessionDetails(sessionId);
        }
      } else {
        const error = await response.json();
        alert(`분석 생성 실패: ${error.message}`);
      }
    } catch (error) {
      console.error('Failed to generate analysis:', error);
      alert('분석 생성 중 오류가 발생했습니다.');
    }
  };

  const resetFilters = () => {
    setSessionStatusFilter('');
    setSessionExamTypeFilter('');
    setSessionSearchEmail('');
    setSessionSearchId('');
    setSessionCurrentPage(1);
    loadExamSessions('', '', 1);
  };

  const getStatusLabel = (status: string) => {
    if (status === 'completed') return '완료';
    if (status === 'active') return '진행중';
    if (status === 'paused') return '일시정지';
    if (status === 'abandoned') return '중단';
    return status;
  };

  const getStatusBadgeClass = (status: string) => {
    if (status === 'completed') return 'success';
    if (status === 'active') return 'warning';
    if (status === 'paused') return 'info';
    if (status === 'abandoned') return 'danger';
    return 'secondary';
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString();
  };

  const getUserLabel = (session: ExamSession) => {
    if (session.user_info?.name || session.user_info?.email) {
      return `${session.user_info.name || '이름 없음'} (${session.user_info.email || session.user_id})`;
    }
    return session.user_email || session.user_id || '알 수 없음';
  };

  useEffect(() => {
    loadExamSessions('', '', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page exam-monitoring-page">
      <header className="admin-page-title">
        <span className="admin-page-title__eyebrow">관리자 워크스페이스</span>
        <h1>시험 모니터링</h1>
        <p>시험 세션 상태, 진행률, 점수, 상세 결과를 확인합니다.</p>
      </header>

      <section className="admin-section exam-monitoring-panel">
        <div className="admin-header exam-monitoring-panel__header">
          <div>
            <h2>시험 세션</h2>
            <p>최근 시험 세션을 상태와 사용자 기준으로 조회합니다.</p>
          </div>
          <button className="admin-btn danger" type="button" onClick={bulkDeleteAbandoned}>
            중단 세션 일괄 삭제
          </button>
        </div>

        <div className="exam-monitoring-controls">
          <div className="admin-form-inline exam-monitoring-controls__group">
            <select
              className="admin-form-control sm select"
              value={sessionStatusFilter}
              onChange={event => setSessionStatusFilter(event.target.value)}
            >
              <option value="">모든 상태</option>
              <option value="active">진행중</option>
              <option value="paused">일시정지</option>
              <option value="completed">완료</option>
              <option value="abandoned">중단</option>
            </select>
            <select
              className="admin-form-control sm select"
              value={sessionExamTypeFilter}
              onChange={event => setSessionExamTypeFilter(event.target.value)}
            >
              <option value="">모든 시험 유형</option>
              <option value="aws-saa-c03">AWS SAA-C03</option>
              <option value="aws-sap-c02">AWS SAP-C02</option>
              <option value="aws-dva-c02">AWS DVA-C02</option>
              <option value="aws-clf-c02">AWS CLF-C02</option>
            </select>
            <input
              type="email"
              className="admin-form-control sm"
              placeholder="이메일 검색"
              value={sessionSearchEmail}
              onChange={event => setSessionSearchEmail(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && loadExamSessions(sessionStatusFilter, sessionSearchEmail, 1)}
            />
            <input
              type="text"
              className="admin-form-control sm"
              placeholder="세션 ID 검색"
              value={sessionSearchId}
              onChange={event => setSessionSearchId(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && loadExamSessions(sessionStatusFilter, sessionSearchEmail, 1)}
            />
            <button className="admin-btn primary" type="button" onClick={() => loadExamSessions(sessionStatusFilter, sessionSearchEmail, 1)}>
              검색
            </button>
            <button className="admin-btn secondary" type="button" onClick={resetFilters}>
              초기화
            </button>
          </div>
        </div>

        <div className="sessions-stats">
          <span>총 {totalSessions}개 세션</span>
          <span>페이지 {sessionCurrentPage} / {totalPages}</span>
          {lastLoadMs !== null && <span>조회 {lastLoadMs}ms</span>}
        </div>

        <div className="exam-monitoring-table-wrap">
          <table className="admin-table exam-monitoring-table">
            <thead>
              <tr>
                <th className="col-session-id">세션 ID</th>
                <th className="col-session-user">사용자</th>
                <th className="col-session-exam">시험 유형</th>
                <th className="col-session-status">상태</th>
                <th className="col-session-progress">진행률</th>
                <th className="col-session-score">점수</th>
                <th className="col-session-started">시작 시간</th>
                <th className="col-session-action">작업</th>
              </tr>
            </thead>
            <tbody>
              {examSessions.length === 0 && (
                <tr>
                  <td colSpan={8} className="admin-empty-cell">
                    {loading ? '시험 세션을 불러오는 중입니다.' : '표시할 시험 세션이 없습니다.'}
                  </td>
                </tr>
              )}
              {examSessions.map(session => (
                <tr key={session.session_id}>
                  <td className="col-session-id">
                    <span className="admin-badge info session-id-badge">#{session.session_id.slice(-8)}</span>
                  </td>
                  <td className="col-session-user">
                    <span className="session-user-cell">{session.user_email || session.user_id || '알 수 없음'}</span>
                  </td>
                  <td className="col-session-exam">
                    <span className="admin-badge secondary">{session.exam_type}</span>
                  </td>
                  <td className="col-session-status">
                    <span className={`admin-badge ${getStatusBadgeClass(session.status)}`}>
                      {getStatusLabel(session.status)}
                    </span>
                  </td>
                  <td className="col-session-progress">
                    <div className="session-progress-cell">
                      <span>{session.current_question || 0}/{session.total_questions || 65}</span>
                      {session.ttl_expires_at && (
                        <small className={session.ttl_expired ? 'admin-text-danger' : 'admin-text-warning'}>
                          {session.ttl_expired ? '만료됨' : `만료 ${formatDateTime(session.ttl_expires_at)}`}
                        </small>
                      )}
                      {session.time_remaining && <small>남은 시간 {Math.floor(session.time_remaining / 60)}분</small>}
                    </div>
                  </td>
                  <td className="col-session-score">
                    {session.score ? <span className="admin-badge success">{session.score}점</span> : <span className="admin-text-gray-500">-</span>}
                  </td>
                  <td className="col-session-started">
                    <span className="admin-text-sm">{formatDateTime(session.started_at || session.created_at)}</span>
                  </td>
                  <td className="col-session-action">
                    <div className="admin-flex">
                      <button className="admin-btn sm primary" type="button" onClick={() => loadSessionDetails(session.session_id)}>
                        상세
                      </button>
                      {session.status === 'completed' ? (
                        <>
                          <button
                            className="admin-btn sm secondary"
                            type="button"
                            onClick={() => window.open(`/results?sessionId=${session.session_id}&userId=${session.user_id}`, '_blank')}
                          >
                            결과
                          </button>
                          <button className="admin-btn sm secondary" type="button" onClick={() => generateAnalysis(session.session_id)}>
                            AI
                          </button>
                        </>
                      ) : (
                        <button className="admin-btn sm secondary" type="button" onClick={() => completeSession(session.session_id)}>
                          완료
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalSessions > SESSIONS_PER_PAGE && (
          <div className="admin-pagination">
            <button
              className="admin-btn secondary"
              type="button"
              onClick={() => loadExamSessions(sessionStatusFilter, sessionSearchEmail, sessionCurrentPage - 1)}
              disabled={sessionCurrentPage === 1}
            >
              이전
            </button>
            <span className="page-info">{sessionCurrentPage} / {totalPages} 페이지</span>
            <button
              className="admin-btn secondary"
              type="button"
              onClick={() => loadExamSessions(sessionStatusFilter, sessionSearchEmail, sessionCurrentPage + 1)}
              disabled={sessionCurrentPage >= totalPages}
            >
              다음
            </button>
          </div>
        )}
      </section>

      {detailsLoading && (
        <div className="modal-overlay">
          <div className="session-modal session-modal--loading">
            <div className="modal-body">세션 상세 정보를 불러오는 중입니다.</div>
          </div>
        </div>
      )}

      {selectedSession && !detailsLoading && (
        <div className="modal-overlay">
          <div className="session-modal">
            <div className="modal-header">
              <h3>시험 세션 상세</h3>
              <button className="modal-close" type="button" onClick={() => setSelectedSession(null)}>
                닫기
              </button>
            </div>
            <div className="modal-body">
              <div className="session-detail-id">세션 ID: {selectedSession.session_id}</div>

              <section className="session-detail-section">
                <h4>기본 정보</h4>
                <div className="session-detail-grid">
                  <InfoItem label="사용자" value={getUserLabel(selectedSession)} />
                  <InfoItem label="시험 유형" value={selectedSession.exam_type} />
                  <InfoItem label="상태" value={getStatusLabel(selectedSession.status)} badgeClass={getStatusBadgeClass(selectedSession.status)} />
                  <InfoItem label="시작 시간" value={formatDateTime(selectedSession.started_at || selectedSession.created_at)} />
                  <InfoItem label="완료 시간" value={formatDateTime(selectedSession.completed_at)} />
                  <InfoItem label="소요 시간" value={`${selectedSession.time_taken_minutes || 0}분`} />
                  <InfoItem label="진행률" value={`${selectedSession.current_question || 0} / ${selectedSession.total_questions || 65}`} />
                  <InfoItem label="최종 점수" value={selectedSession.score ? `${selectedSession.score}점` : '-'} />
                </div>
              </section>

              {selectedSession.questions?.length ? (
                <section className="session-detail-section">
                  <h4>문제 정보</h4>
                  <div className="session-detail-grid">
                    <InfoItem label="총 문제 수" value={`${selectedSession.questions.length}개`} />
                    {selectedSession.status === 'completed' && (
                      <>
                        <InfoItem label="정답 수" value={`${selectedSession.questions.filter(question => question.is_correct).length}개`} />
                        <InfoItem label="정답률" value={`${Math.round((selectedSession.questions.filter(question => question.is_correct).length / selectedSession.questions.length) * 100)}%`} />
                      </>
                    )}
                  </div>
                </section>
              ) : null}
            </div>
            <div className="modal-footer">
              {selectedSession.status === 'completed' && (
                <>
                  <button
                    className="admin-btn primary"
                    type="button"
                    onClick={() => window.open(`/results?sessionId=${selectedSession.session_id}&userId=${selectedSession.user_id}`, '_blank')}
                  >
                    결과보기
                  </button>
                  <button className="admin-btn secondary" type="button" onClick={() => generateAnalysis(selectedSession.session_id)}>
                    AI 분석 생성
                  </button>
                </>
              )}
              <button className="admin-btn danger" type="button" onClick={() => deleteSession(selectedSession.session_id)}>
                삭제
              </button>
              <button className="admin-btn secondary" type="button" onClick={() => setSelectedSession(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem: React.FC<{ label: string; value: string; badgeClass?: string }> = ({ label, value, badgeClass }) => (
  <div className="session-detail-item">
    <span>{label}</span>
    {badgeClass ? <strong><span className={`admin-badge ${badgeClass}`}>{value}</span></strong> : <strong>{value}</strong>}
  </div>
);

export default ExamMonitoringPage;
